/**
 * Sortable draft row with left-side drag handle.
 * For Media Devoted | Created By Ciro Autiero
 */

import { forwardRef, type FC } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { cn } from '@/lib/utils';

import { PreparedDropsDraftsListRow } from './PreparedDropsDraftsListRow';
import type { PreparedDropsDraftsListRowProps } from './PreparedDropsDraftsListRow';

export interface SortableDraftRowProps extends PreparedDropsDraftsListRowProps {
  isReorderPending?: boolean;
}

/** Presentational overlay for drag preview; follows cursor via DragOverlay. */
export const DraftRowOverlay: FC<PreparedDropsDraftsListRowProps> = ({ drop, ...rowProps }) => (
  <div className="flex w-full items-stretch rounded border border-border bg-background shadow-lg">
    <div className="flex touch-none shrink-0 cursor-grabbing items-center justify-center px-2 text-muted-foreground">
      <GripVertical className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <PreparedDropsDraftsListRow drop={drop} {...rowProps} />
    </div>
  </div>
);

export const SortableDraftRow = forwardRef<HTMLDivElement, SortableDraftRowProps>(
  ({ drop, isReorderPending = false, ...rowProps }, ref) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
      id: drop.id,
    });
    const style = {
      transform: CSS.Transform.toString(transform),
    };
    const setRef = (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };
    return (
      <div
        ref={setRef}
        style={style}
        className={cn('flex w-full items-stretch', isDragging && 'invisible')}
      >
        <button
          type="button"
          className={cn(
            'flex touch-none shrink-0 cursor-grab items-center justify-center px-2 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            isDragging && 'cursor-grabbing',
            isReorderPending && 'pointer-events-none opacity-50',
          )}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <PreparedDropsDraftsListRow drop={drop} {...rowProps} />
        </div>
      </div>
    );
  },
);
SortableDraftRow.displayName = 'SortableDraftRow';
